import { useCallback, useEffect, useRef, useState } from "react";
import { AIDE_WS_BASE, UNIFIED_STREAM_PATH } from "./env";
import type { UnifiedEvent } from "./agent-graph-types";

/**
 * UC-11 hook: subscribes to /api/v1/lifestyle/triton/unified_stream
 * for one turn_id. Streams graph.* + pact.* events into the
 * caller's reducer; replays the ring buffer on first connect so a
 * late subscriber catches up.
 *
 * Idle when `turn_id` is null/empty. On turn change the previous
 * socket is closed cleanly.
 */
export interface UseUnifiedStreamOptions {
  turnId: string | null;
  dealId?: string | null | undefined;
  onEvent: (event: UnifiedEvent) => void;
  onError?: ((message: string) => void) | undefined;
}

export interface UseUnifiedStreamResult {
  connected: boolean;
  /** Force-close the current socket (useful when a turn aborts). */
  close: () => void;
}

export function useUnifiedStream(
  options: UseUnifiedStreamOptions,
): UseUnifiedStreamResult {
  const { turnId, dealId, onEvent, onError } = options;
  const [connected, setConnected] = useState<boolean>(false);
  const socketRef = useRef<WebSocket | null>(null);
  const eventRef = useRef(onEvent);
  const errorRef = useRef(onError);
  eventRef.current = onEvent;
  errorRef.current = onError;

  const close = useCallback(() => {
    const sock = socketRef.current;
    socketRef.current = null;
    if (sock !== null && sock.readyState !== WebSocket.CLOSED) {
      try {
        sock.close(1000, "client_close");
      } catch {
        /* swallow */
      }
    }
    setConnected(false);
  }, []);

  useEffect(() => {
    if (!turnId) {
      close();
      return;
    }
    const qs = new URLSearchParams({ turn_id: turnId });
    if (dealId) qs.set("deal_id", dealId);
    const url = `${AIDE_WS_BASE}${UNIFIED_STREAM_PATH}?${qs.toString()}`;
    let cancelled = false;
    let sock: WebSocket;
    try {
      sock = new WebSocket(url);
    } catch (err) {
      errorRef.current?.(
        `unified-stream: failed to open socket: ${String(err)}`,
      );
      return;
    }
    socketRef.current = sock;

    sock.onopen = () => {
      if (cancelled) return;
      setConnected(true);
    };
    sock.onmessage = (msg) => {
      if (cancelled) return;
      let parsed: UnifiedEvent | null = null;
      try {
        parsed = JSON.parse(String(msg.data)) as UnifiedEvent;
      } catch {
        errorRef.current?.(
          `unified-stream: non-JSON frame: ${String(msg.data).slice(0, 120)}`,
        );
        return;
      }
      if (!parsed) return;
      if (parsed.topic_class === "error") {
        errorRef.current?.(
          `unified-stream error ${parsed.code}: ${parsed.message}`,
        );
        return;
      }
      eventRef.current(parsed);
    };
    sock.onerror = () => {
      if (cancelled) return;
      errorRef.current?.("unified-stream: socket error");
    };
    sock.onclose = () => {
      if (cancelled) return;
      setConnected(false);
    };

    return () => {
      cancelled = true;
      if (
        sock.readyState === WebSocket.OPEN ||
        sock.readyState === WebSocket.CONNECTING
      ) {
        try {
          sock.close(1000, "effect_cleanup");
        } catch {
          /* swallow */
        }
      }
      if (socketRef.current === sock) {
        socketRef.current = null;
      }
    };
  }, [turnId, dealId, close]);

  return { connected, close };
}
